/**
 * MCP（Model Context Protocol）HTTP 客户端
 *
 * 所有请求通过 /api/mcp-relay 服务端中转，解决跨域问题。
 * 协议：JSON-RPC 2.0，符合 MCP spec 2024-11-05。
 */

import type { MCPServerConfig, MCPToolDef } from '../stores/mcp'

import { useOfflineSyncStore } from '../stores/offlineSync'

/** MCP 中转端点（服务端代理到目标 MCP 服务器） */
const MCP_RELAY_PATH = '/api/mcp-relay'

/**
 * 获取设备鉴权 token（与 push-server 同源，用于 mcp-relay 鉴权）。
 * relay 与 offlineSync 走同一个 push-server：若 token 尚未注册，先跑一次幂等的 autoInit 补齐。
 */
async function getRelayAuthToken(): Promise<string> {
  const offlineSync = useOfflineSyncStore()
  if (!offlineSync.userToken) {
    // 首屏 3s 内 autoInit 可能还没注册出 token，这里主动补一次（幂等）
    await offlineSync.autoInit().catch(() => {})
  }
  return offlineSync.userToken
}

/** JSON-RPC 2.0 请求体 */
interface MCPRequest {
  jsonrpc: '2.0'
  id: number
  method: string
  params?: unknown
}

/** JSON-RPC 2.0 响应体 */
interface MCPResponse<T = unknown> {
  jsonrpc: '2.0'
  id: number
  result?: T
  error?: { code: number, message: string, data?: unknown }
}

/** initialize 响应 */
interface InitializeResult {
  protocolVersion: string
  capabilities: Record<string, unknown>
  serverInfo?: { name: string, version: string }
}

/** tools/list 响应 */
interface ToolsListResult {
  tools: Array<{
    name: string
    description?: string
    inputSchema: Record<string, unknown>
  }>
}

/** tools/call 响应 */
interface ToolCallResult {
  content: Array<{ type: string, text?: string, [key: string]: unknown }>
  isError?: boolean
}

let _requestId = 1
function nextId(): number {
  return _requestId++
}

/**
 * 通过服务端中转发送 MCP JSON-RPC 请求
 */
async function relay<T>(
  server: MCPServerConfig,
  method: string,
  params?: unknown,
): Promise<T> {
  const body: { targetUrl: string, mcpRequest: MCPRequest, apiKey?: string, headers?: Record<string, string> } = {
    targetUrl: server.url,
    mcpRequest: {
      jsonrpc: '2.0',
      id: nextId(),
      method,
      ...(params !== undefined ? { params } : {}),
    },
  }
  // 只在有值时传递，避免服务端日志记录空字段
  if (server.apiKey)
    body.apiKey = server.apiKey
  if (server.headers && Object.keys(server.headers).length > 0)
    body.headers = server.headers

  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const token = await getRelayAuthToken()
  if (token)
    headers.Authorization = `Bearer ${token}`

  const response = await fetch(MCP_RELAY_PATH, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    const text = await response.text().catch(() => response.statusText)
    throw new Error(`MCP relay HTTP ${response.status}: ${text}`)
  }

  const json = await response.json() as MCPResponse<T>

  if (json.error) {
    throw new Error(`MCP error ${json.error.code}: ${json.error.message}`)
  }

  return json.result as T
}

/**
 * MCP 客户端 Composable
 *
 * @example
 * const client = useMCPClient()
 * const tools = await client.listTools(server)
 * const result = await client.callTool(server, 'order_coffee', { size: 'large' })
 */
export function useMCPClient() {
  /**
   * 握手：验证连接可用性
   * 返回服务器协议版本信息
   */
  async function initialize(server: MCPServerConfig): Promise<InitializeResult> {
    return relay<InitializeResult>(server, 'initialize', {
      protocolVersion: '2024-11-05',
      capabilities: {},
      clientInfo: { name: 'MeowChat', version: '1.0.0' },
    })
  }

  /**
   * 拉取工具列表（用于本地缓存）
   */
  async function listTools(server: MCPServerConfig): Promise<MCPToolDef[]> {
    const result = await relay<ToolsListResult>(server, 'tools/list')
    return (result.tools ?? []).map(t => ({
      name: t.name,
      description: t.description ?? '',
      inputSchema: t.inputSchema ?? { type: 'object', properties: {} },
    }))
  }

  /**
   * 调用工具，返回文本内容（聚合 content 数组中的 text 字段）
   */
  async function callTool(
    server: MCPServerConfig,
    toolName: string,
    toolArgs: Record<string, unknown>,
  ): Promise<string> {
    const result = await relay<ToolCallResult>(server, 'tools/call', {
      name: toolName,
      arguments: toolArgs,
    })

    if (result.isError) {
      const errText = result.content
        .filter(c => c.type === 'text')
        .map(c => c.text ?? '')
        .join('\n')
      throw new Error(errText || 'Tool returned an error')
    }

    return result.content
      .filter(c => c.type === 'text')
      .map(c => c.text ?? '')
      .join('\n')
  }

  return { initialize, listTools, callTool }
}
